import SLAForm from "@/components/slas/SLAForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import projectService from "@/services/project";
import slaService from "@/services/sla";
import { Project } from "@/types/project";
import { SLA } from "@/types/sla";
import { ArrowLeft, Clock, MoreHorizontal, Pencil, PlusCircle, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function SLAs() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { projectId, companyId } = useParams<{ projectId: string, companyId: string }>();

  const [slas, setSLAs] = useState<SLA[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [totalSLAs, setTotalSLAs] = useState(0);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editingSLA, setEditingSLA] = useState<SLA | undefined>(undefined);

  const fetchProject = async () => {
    if (!projectId) return;

    try {
      // Primeiro, buscamos o projeto usando a rota alternativa
      const [initialData, initialError] = await projectService.getById(companyId, projectId);

      if (initialError) {
        toast({
          title: "Erro",
          description: initialError.msg || "Erro ao carregar projeto",
          variant: "destructive",
        });
        return;
      }

      // Definimos o projeto com os dados iniciais
      setProject(initialData);

      // Agora que temos o clientId, podemos buscar novamente com a rota correta
      if (initialData && initialData.clientId) {
        const [completeData, completeError] = await projectService.getById(initialData.clientId, projectId);

        if (!completeError && completeData) {
          // Atualizamos o projeto com os dados completos
          setProject(completeData);
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao carregar o projeto",
        variant: "destructive",
      });
    }
  };

  const fetchSLAs = async () => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      const [data, error] = await slaService.getAllByProject(projectId, search, page, limit);

      if (error) {
        toast({
          title: "Erro",
          description: error.msg || "Erro ao carregar SLAs",
          variant: "destructive",
        });
        setSLAs([]);
        setTotalSLAs(0);
        return;
      }

      if (data && data.result) {
        setSLAs(data.result);
        setTotalSLAs(data.total || 0);
      } else {
        console.error("Resposta da API em formato inesperado:", data);
        setSLAs([]);
        setTotalSLAs(0);
        toast({
          title: "Aviso",
          description: "Nenhum SLA encontrado para este projeto",
        });
      }
    } catch (error) {
      console.error("Erro ao carregar SLAs:", error);
      setSLAs([]);
      setTotalSLAs(0);
      toast({
        title: "Erro",
        description: "Falha ao carregar os SLAs",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    fetchSLAs();
  }, [projectId, page, limit, search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleCreateSLA = () => {
    setFormMode("create");
    setEditingSLA(undefined);
    setFormOpen(true);
  };

  const handleEditSLA = (sla: SLA) => {
    setFormMode("edit");
    setEditingSLA(sla);
    setFormOpen(true);
  };

  const handleBackToProjects = () => {
    if (project) {
      navigate(`/companies/${project.clientId}/projects`);
    } else {
      navigate("/companies");
    }
  };

  // Função para obter a cor do badge com base na prioridade
  const getPriorityColor = (priority: string): "default" | "destructive" | "secondary" | "outline" => {
    switch (priority) {
      case "low":
        return "secondary";
      case "medium":
        return "default";
      case "high":
        return "destructive";
      case "critical":
        return "destructive";
      default:
        return "outline";
    }
  };

  // Função para exibir o texto da prioridade
  const getPriorityText = (priority: string): string => {
    switch (priority) {
      case "low":
        return "Baixa";
      case "medium":
        return "Média";
      case "high":
        return "Alta";
      case "critical":
        return "Crítica";
      default:
        return priority;
    }
  };

  // Função para exibir o texto do tipo
  const getTypeText = (type: string): string => {
    switch (type) {
      case "response_time":
        return "Tempo de Resposta";
      case "resolution_time":
        return "Tempo de Resolução";
      default:
        return type;
    }
  };

  if (!projectId) {
    return (
      <div className="flex h-screen">

        <main className="flex-1 p-6 overflow-auto">
          <div className="flex items-center mb-6">
            <Button variant="ghost" onClick={() => navigate("/clients")} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <h1 className="text-2xl font-bold flex-1">
              SLAs
            </h1>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground text-center">
                Projeto não encontrado. Selecione um projeto para visualizar seus SLAs.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => navigate("/clients")}>
                Ir para Clientes
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center mb-6">
        <Button variant="ghost" onClick={handleBackToProjects} className="mr-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-2xl font-bold flex-1">
          SLAs {project ? `do Projeto ${project.name}` : ''}
        </h1>
        <Button onClick={handleCreateSLA}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Novo SLA
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de SLAs</CardTitle>
          <CardDescription>
            Gerencie os acordos de nível de serviço para este projeto
          </CardDescription>
          <div className="flex items-center mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar SLAs..."
                className="pl-8"
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Prioridade</TableHead>
                <TableHead>Tempo (min)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!slas || slas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    {isLoading
                      ? "Carregando SLAs..."
                      : "Nenhum SLA encontrado"}
                  </TableCell>
                </TableRow>
              ) : (
                slas.map((sla) => (
                  <TableRow key={sla._id}>
                    <TableCell className="font-medium">{sla.name}</TableCell>
                    <TableCell>{getTypeText(sla.type)}</TableCell>
                    <TableCell>
                      <Badge variant={getPriorityColor(sla.priority)}>
                        {getPriorityText(sla.priority)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        {sla.timeInMinutes}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={sla.isActive ? "default" : "secondary"}>
                        {sla.isActive ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Abrir menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ações</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleEditSLA(sla)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-sm text-muted-foreground">
            Mostrando {slas?.length || 0} de {totalSLAs} SLAs
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1 || isLoading}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={!slas || slas.length < limit || isLoading}
            >
              Próxima
            </Button>
          </div>
        </CardFooter>
      </Card>

      {formOpen && projectId && (
        <SLAForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSuccess={fetchSLAs}
          initialData={editingSLA}
          mode={formMode}
          projectId={projectId}
        />
      )}
    </>
  );
}
